import { demoPick, inWindow, suffixMatch, type DigitWindow } from './rulesWindow.ts';

/**
 * 「How it works」弹层的示例号码。全部由**最近一期真实开奖号**推导，页面不编号码：
 * 一个从没开出过的号配上「可去链上核对」的页脚，是在自己拆台。
 *
 * <p>正例保留末 N−2 位（5 位档中 3、4 位档中 2），紧挨着命中段的那一位一定不同——
 * 否则会意外多中一位，标的钱和实际能拿的钱对不上（`demoPick` 的约束）。
 *
 * <p>反例是这一段存在的主要理由：玩家默认按「对上几个数字」理解。反例把**倒数第二位**
 * 改掉，其余全同——前面明明都对，却只算 1 位。这比「末位错、0 位」更能戳中误解：
 * 那个版本玩家会觉得「反正一个都没中」，学不到「前面对了不算」。
 */
export type HowDemo = {
  yours: string;
  hits: number;
  broken: string;
  brokenHits: number;
};

export function howDemo(result: string): HowDemo | null {
  const digits = result.length;
  if (digits < 3) return null;
  const hits = digits - 2;
  const yours = demoPick(result, hits);
  const chars = result.split('');
  const at = digits - 2;
  chars[at] = String((Number(chars[at]) + 3) % 10);
  const broken = chars.join('');
  return {
    yours,
    hits: suffixMatch(yours, result),
    broken,
    brokenHits: suffixMatch(broken, result),
  };
}

export type TenDigitCell = { digit: string; inWindow: boolean };

/**
 * D₁..D₁₀ 十格。归档行（`draw_digits`）齐了就整行都画；没到或者和开奖号对不上
 * （窗口配置改过、行不完整）就只画窗口里那几位，其余留空——宁可少画，也不画一张
 * 和屏幕上的开奖号自相矛盾的表。
 */
export function tenDigitCells(archiveDigits: string, result: string, window: DigitWindow): TenDigitCell[] {
  const [start, end] = window;
  const full = archiveDigits.length === 10
    && archiveDigits.slice(start - 1, end) === result;
  return Array.from({ length: 10 }, (_, i) => {
    const position = i + 1;
    const on = inWindow(position, window);
    const digit = full
      ? archiveDigits[i]
      : on && result.length === end - start + 1
        ? result[position - start]
        : '';
    return { digit, inWindow: on };
  });
}
